import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown } from "lucide-react";
import { church, nav } from "@/config/church";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setOpenDropdown(null); }, [pathname]);

  const handleEnter = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  };

  const isActiveGroup = (item) =>
    item.children?.some((c) => c.to === pathname) || item.to === pathname;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/[0.06] shadow-[0_10px_30px_-20px_rgba(0,0,0,0.25)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-npbc flex items-center justify-between py-3 md:py-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={church.logo}
            alt="New Philadelphia Missionary Baptist Church logo"
            className="h-12 w-12 md:h-14 md:w-14 object-contain drop-shadow-sm"
            loading="eager"
          />
          <div className="block leading-tight">
            <div className={`heading-serif text-[13px] sm:text-[15px] md:text-[17px] font-bold ${scrolled ? "text-npbc-black" : "text-white drop-shadow"}`}>
              New Philadelphia
            </div>
            <div className={`text-[9px] sm:text-[10px] md:text-[11px] font-semibold tracking-[0.22em] sm:tracking-[0.24em] uppercase ${scrolled ? "text-npbc-red" : "text-npbc-gold drop-shadow"}`}>
              Missionary Baptist
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleEnter(item.label)}
                onMouseLeave={handleLeave}
              >
                <button
                  type="button"
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={`relative inline-flex items-center gap-1 px-3.5 py-2 text-[13.5px] font-semibold tracking-wide transition-colors duration-300 ${
                    scrolled
                      ? isActiveGroup(item) ? "text-npbc-red" : "text-npbc-gray hover:text-npbc-red"
                      : isActiveGroup(item) ? "text-white" : "text-white/85 hover:text-white"
                  }`}
                >
                  {item.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openDropdown === item.label ? "rotate-180" : ""}`} />
                </button>
                <div
                  className={`absolute left-0 top-full pt-3 transition-all duration-200 ${
                    openDropdown === item.label ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-1"
                  }`}
                >
                  <div className="min-w-[240px] rounded-2xl bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] border border-black/[0.06] p-2">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition ${
                            isActive ? "bg-npbc-red/10 text-npbc-red" : "text-npbc-black hover:bg-npbc-cream"
                          }`
                        }
                      >
                        {c.label}
                        <ChevronRight className="h-3.5 w-3.5 opacity-40" />
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 text-[13.5px] font-semibold tracking-wide transition-colors duration-300 ${
                    scrolled
                      ? isActive ? "text-npbc-red" : "text-npbc-gray hover:text-npbc-red"
                      : isActive ? "text-white" : "text-white/85 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/visit" data-testid="nav-plan-visit-btn" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm">
            Plan Your Visit
            <ChevronRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className={`lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full transition ${
              scrolled ? "bg-npbc-black text-white" : "bg-white/15 text-white backdrop-blur border border-white/25"
            }`}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu — flat list, dropdown children inline */}
      <div
        data-testid="nav-mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"
        } bg-white border-t border-black/[0.06]`}
      >
        <div className="container-npbc py-5">
          <nav className="flex flex-col divide-y divide-black/[0.06]">
            {nav.flatMap((item) =>
              item.children
                ? item.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`nav-mobile-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between py-3.5 heading-serif text-xl ${isActive ? "text-npbc-red" : "text-npbc-black"}`
                      }
                    >
                      <span>{c.label}</span>
                      <ChevronRight className="h-4 w-4 opacity-40" />
                    </NavLink>
                  ))
                : [
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between py-3.5 heading-serif text-xl ${isActive ? "text-npbc-red" : "text-npbc-black"}`
                      }
                    >
                      <span>{item.label}</span>
                      <ChevronRight className="h-4 w-4 opacity-40" />
                    </NavLink>,
                  ]
            )}
          </nav>
          <Link to="/visit" data-testid="nav-mobile-plan-visit" className="btn-primary mt-6 w-full justify-center">
            Plan Your Visit
          </Link>
        </div>
      </div>
    </header>
  );
}
